import { GenAIResponse } from '@lemoncloud/ssocio-chatbots-api/dist/lib/openai/openai-types';

import type { ListResult, Params } from '@codes/shared';
import { deleteUndefinedProperty } from '@codes/utils';
import { webCore } from '@codes/web-core';

import { ClonePromptDTO, GenerateAIResponseDTO, RevisionRequestView } from '../types';

import type { CreateAgentDTO, CreatePromptDTO, UpdateAgentDTO, UpdatePromptDTO } from '../types';
import type { AgentView, PromptView } from '@lemoncloud/ssocio-chatbots-api';

const CHATBOTS_ENDPOINT = import.meta.env.VITE_CHATBOTS_API_ENDPOINT.toLowerCase();

export const fetchAgents = async (params: Params): Promise<ListResult<AgentView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${CHATBOTS_ENDPOINT}/agents/0/list`,
        })
        .setParams(params)
        .execute<ListResult<AgentView>>();

    return { ...data };
};

export const fetchAgentById = async (agentId: string): Promise<AgentView> => {
    if (!agentId) {
        throw new Error('fetchAgentById: @agentId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${CHATBOTS_ENDPOINT}/agents/${agentId}`,
        })
        .execute<AgentView>();

    return { ...data };
};

export const createAgent = async (createBody: CreateAgentDTO): Promise<AgentView> => {
    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${CHATBOTS_ENDPOINT}/agents/0`,
        })
        .setBody(createBody)
        .execute<AgentView>();

    return response.data;
};

export const deleteAgent = async (agentId: string): Promise<AgentView> => {
    if (!agentId) {
        throw new Error('deleteAgent: @agentId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${CHATBOTS_ENDPOINT}/agents/${agentId}`,
        })
        .setBody({})
        .execute<AgentView>();

    return data;
};

export const updateAgent = async (updateBody: UpdateAgentDTO): Promise<AgentView> => {
    const { agentId, ...data } = updateBody;
    if (!agentId) {
        throw new Error('updateAgent: @agentId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${CHATBOTS_ENDPOINT}/agents/${agentId}`,
        })
        .setBody(data)
        .execute<AgentView>();

    return response.data;
};

export const fetchPrompts = async (params: Params): Promise<ListResult<PromptView>> => {
    const response = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/0/list`,
        })
        .setParams({ ...params })
        .execute<ListResult<PromptView>>();

    return response.data;
};

export const fetchDefaultPrompts = async (params: Params): Promise<ListResult<PromptView>> => {
    const response = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/0`,
        })
        .setParams(deleteUndefinedProperty({ ...params }))
        .execute<PromptView>();

    return {
        total: 1,
        page: 0,
        limit: 10,
        list: [{ ...response.data, id: `${response.data.id}_${params.type || 'unknown'}`, stereo: params.type }],
        aggr: [],
    };
};

export const fetchPromptById = async (promptId: string): Promise<PromptView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/${promptId}`,
        })
        .execute<PromptView>();

    return { ...data };
};

export const createPrompt = async (createBody: CreatePromptDTO): Promise<PromptView> => {
    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/0`,
        })
        .setBody(createBody)
        .execute<PromptView>();

    return response.data;
};

export const deletePrompt = async (promptId: string): Promise<PromptView> => {
    if (!promptId) {
        throw new Error('deletePrompt: @promptId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/${promptId}`,
        })
        .setBody({})
        .execute<PromptView>();

    return data;
};

export const revisePrompt = async (updateBody: UpdatePromptDTO): Promise<RevisionRequestView<PromptView>> => {
    const { promptId, ...data } = updateBody;
    if (!promptId) {
        throw new Error('revisePrompt: @promptId is required');
    }

    // NOTE: 프롬프트 수정 / 이력(revision) 생성
    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/${promptId}/revise`,
        })
        .setBody(data)
        .execute<RevisionRequestView<PromptView>>();

    return response.data;
};

export const fetchRevisionList = async (params: Params): Promise<ListResult<PromptView>> => {
    const { promptId, ...data } = params;
    if (!promptId) {
        throw new Error('fetchRevisionList: @promptId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/${promptId}/child`,
        })
        .setParams({ ...data, revise: '' })
        .execute<ListResult<PromptView>>();

    return {
        ...response.data,
        list: response.data.list?.filter(item => !item.hidden) || [],
    };
};

export const generateAIResponse = async (dto: GenerateAIResponseDTO): Promise<GenAIResponse> => {
    const { agentId, ...data } = dto;
    if (!agentId) {
        throw new Error('generateAIResponse: @agentId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${CHATBOTS_ENDPOINT}/agents/${agentId}/generate`,
        })
        .setBody(data)
        .execute<GenAIResponse>();

    return response.data;
};

export const clonePrompt = async (cloneBody: ClonePromptDTO): Promise<RevisionRequestView<PromptView>> => {
    const { promptId, ...data } = cloneBody;
    if (!promptId) {
        throw new Error('clonePrompt: @promptId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${CHATBOTS_ENDPOINT}/prompts/${promptId}/clone`,
        })
        .setBody(data)
        .execute<RevisionRequestView<PromptView>>();

    return response.data;
};
